import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import LinearProgress from "@mui/material/LinearProgress";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import React from "react";
import axios from "axios";

interface Props {
  onResult: (result: any, ad: any) => void;
  prefill?: {
    imageUrl?: string;
    creativeId?: number;
    advertiserId?: number;
    brandName?: string;
    vertical?: string;
    adSize?: string;
  };
}

function apiUrl(path: string): string {
  const host = window.location.hostname;
  const port = window.location.port;
  const base = port ? `//${host}:${port}` : `//${host}`;
  return `${base}/copilot/genesys/adeval${path}`;
}

const AD_SIZES = ["300x250", "728x90", "160x600", "300x600", "320x50", "970x250"];

const VERTICALS = [
  "Accountant/Tax Services", "Attorney/Legal Services", "Auto Dealers",
  "Banks/Loans/Credit Unions", "Cannabis Products", "Charitable Organizations",
  "Dentists", "Electrical Services", "Fast Food", "Food Products",
  "Furniture Stores", "HVAC Services", "Health Insurance", "Health/Fitness",
  "Home Repair/Hardware", "Insurance", "Jewelry", "Museum",
  "Plumbing/Heating/AC", "Real Estate", "Restaurants",
  "Retirement/Nursing Homes & Services", "Shoes", "Sporting Goods/Bicycles",
];

const AdevalRefresh = ({ onResult, prefill }: Props) => {
  const [imageUrl, setImageUrl] = React.useState(prefill?.imageUrl || "");
  const [creativeId, setCreativeId] = React.useState(prefill?.creativeId?.toString() || "");
  const [advertiserId, setAdvertiserId] = React.useState(prefill?.advertiserId?.toString() || "");
  const [brandName, setBrandName] = React.useState(prefill?.brandName || "");
  const [vertical, setVertical] = React.useState(prefill?.vertical || "");
  const [adSize, setAdSize] = React.useState(prefill?.adSize || "");
  const [generateBg, setGenerateBg] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState("");
  const [result, setResult] = React.useState<any>(null);

  React.useEffect(() => {
    if (prefill?.imageUrl) setImageUrl(prefill.imageUrl);
    if (prefill?.creativeId) setCreativeId(prefill.creativeId.toString());
    if (prefill?.advertiserId) setAdvertiserId(prefill.advertiserId.toString());
    if (prefill?.brandName) setBrandName(prefill.brandName);
    if (prefill?.vertical) setVertical(prefill.vertical);
    if (prefill?.adSize) setAdSize(prefill.adSize);
  }, [prefill]);

  const handleSubmit = async () => {
    if (!imageUrl && !creativeId) return;
    setRefreshing(true);
    setError("");
    setResult(null);
    try {
      const payload: any = {
        refLimit: 5,
        generateBackground: generateBg,
      };
      if (imageUrl) payload.imageUrl = imageUrl;
      if (creativeId) payload.creativeId = Number(creativeId);
      if (advertiserId) payload.advertiserId = Number(advertiserId);
      if (brandName) payload.brandName = brandName;
      if (vertical) payload.vertical = vertical;
      if (adSize) payload.adSize = adSize;

      const res = await axios.post(apiUrl("/refresh"), payload);
      if (res.data?.error) {
        setError(res.data.error);
      } else {
        setResult(res.data);
        onResult(res.data, {
          campaignName: res.data.brandName || brandName || "Refreshed Ad",
          bgImageUrl: imageUrl || res.data.originalImageUrl,
        });
      }
    } catch (e: any) {
      setError(e.response?.data?.detail || e.message || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const canSubmit = (imageUrl || creativeId) && !refreshing;

  if (result) {
    return <RefreshResult data={result} />;
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {refreshing && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Analyzing ad and generating refresh spec... this takes 20-30 seconds
          </Typography>
        </Box>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Provide an image URL or Xandr creative ID. The system will analyze the current ad,
        pull CRM context, and generate a refreshed composition spec.
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
        <TextField
          label="Image URL"
          value={imageUrl}
          onChange={(e) => setImageUrl(e.target.value)}
          size="small"
          placeholder="https://..."
          fullWidth
        />

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <TextField
            label="Creative ID (Xandr)"
            value={creativeId}
            onChange={(e) => setCreativeId(e.target.value)}
            size="small"
            type="number"
            sx={{ minWidth: 180 }}
          />
          <TextField
            label="Advertiser ID"
            value={advertiserId}
            onChange={(e) => setAdvertiserId(e.target.value)}
            size="small"
            type="number"
            sx={{ minWidth: 180 }}
          />
          <TextField
            label="Brand Name"
            value={brandName}
            onChange={(e) => setBrandName(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <Autocomplete
            freeSolo
            options={VERTICALS}
            value={vertical}
            onChange={(_e, val) => setVertical(val || "")}
            onInputChange={(_e, val) => setVertical(val || "")}
            size="small"
            sx={{ minWidth: 250 }}
            renderInput={(params) => (
              <TextField {...params} label="Vertical" placeholder="e.g. Auto Dealers" />
            )}
          />
          <Autocomplete
            freeSolo
            options={AD_SIZES}
            value={adSize}
            onChange={(_e, val) => setAdSize(val || "")}
            onInputChange={(_e, val) => setAdSize(val || "")}
            size="small"
            sx={{ minWidth: 160 }}
            renderInput={(params) => <TextField {...params} label="Ad Size" />}
          />
          <FormControlLabel
            control={<Switch checked={generateBg} onChange={(e) => setGenerateBg(e.target.checked)} />}
            label="Generate background"
          />
        </Box>
      </Box>

      <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit} size="large">
        Refresh Ad
      </Button>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Refresh result display
// ---------------------------------------------------------------------------

function colorSwatch(hex: string, label: string) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: hex, border: "1px solid #555" }} />
      <Typography variant="body2">{label}: {hex}</Typography>
    </Box>
  );
}

const RefreshResult = ({ data }: { data: any }) => {
  const comp = data.composition || {};
  const palette = comp.colorPalette || {};
  const crm = data.crmContext || {};

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Header row: original ad + composition preview */}
      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
        {/* Original ad */}
        {data.originalImageUrl && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Original Ad
            </Typography>
            <img
              src={data.originalImageUrl}
              alt="Original"
              style={{ maxWidth: 300, maxHeight: 250, objectFit: "contain", borderRadius: 4, border: "1px solid #444" }}
            />
          </Box>
        )}

        {/* Generated background */}
        {comp.backgroundImageUrl && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Generated Background
            </Typography>
            <img
              src={comp.backgroundImageUrl}
              alt="Background"
              style={{ maxWidth: 300, maxHeight: 250, objectFit: "contain", borderRadius: 4, border: "1px solid #444" }}
            />
          </Box>
        )}
      </Box>

      {/* Metadata chips */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        {data.brandName && <Chip label={data.brandName} size="small" />}
        {data.vertical && <Chip label={data.vertical} size="small" variant="outlined" />}
        {data.adSize && <Chip label={data.adSize} size="small" variant="outlined" />}
        {data.referencesUsed > 0 && (
          <Chip label={`${data.referencesUsed} references`} size="small" variant="outlined" />
        )}
      </Box>

      {/* Composition spec */}
      <Box>
        <Typography variant="h6" gutterBottom>Refreshed Copy</Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pl: 1 }}>
          <Typography variant="body1">
            <strong>Headline:</strong> {comp.headline}
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              ({(comp.headline || "").length}/26)
            </Typography>
          </Typography>
          <Typography variant="body1">
            <strong>Subheadline:</strong> {comp.subheadline}
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              ({(comp.subheadline || "").length}/42)
            </Typography>
          </Typography>
          <Typography variant="body1">
            <strong>CTA:</strong> {comp.ctaText}
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              ({(comp.ctaText || "").length}/15)
            </Typography>
          </Typography>
        </Box>
      </Box>

      {/* Color palette */}
      <Box>
        <Typography variant="h6" gutterBottom>Color Palette</Typography>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", pl: 1 }}>
          {palette.primary && colorSwatch(palette.primary, "Primary")}
          {palette.secondary && colorSwatch(palette.secondary, "Secondary")}
          {palette.accent && colorSwatch(palette.accent, "Accent")}
          {palette.background && colorSwatch(palette.background, "Background")}
          {palette.ctaBackground && colorSwatch(palette.ctaBackground, "CTA Bg")}
          {palette.ctaText && colorSwatch(palette.ctaText, "CTA Text")}
        </Box>
      </Box>

      {/* Layout + mood */}
      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
        {comp.layout && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Layout</Typography>
            <Typography variant="body1">{comp.layout}</Typography>
          </Box>
        )}
        {comp.backgroundMood && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Mood</Typography>
            <Typography variant="body1">{comp.backgroundMood}</Typography>
          </Box>
        )}
      </Box>

      {/* Rationale */}
      {comp.refreshRationale && (
        <Box>
          <Typography variant="h6" gutterBottom>Refresh Rationale</Typography>
          <Typography variant="body1" sx={{ pl: 1 }}>{comp.refreshRationale}</Typography>
        </Box>
      )}

      {/* Keep / Changed */}
      <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {comp.keepFromOriginal?.length > 0 && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Kept from Original</Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {comp.keepFromOriginal.map((item: string, i: number) => (
                <li key={i}><Typography variant="body2">{item}</Typography></li>
              ))}
            </ul>
          </Box>
        )}
        {comp.changed?.length > 0 && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Changed</Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {comp.changed.map((item: string, i: number) => (
                <li key={i}><Typography variant="body2">{item}</Typography></li>
              ))}
            </ul>
          </Box>
        )}
      </Box>

      {/* CRM context summary */}
      {crm.advertiser && (
        <Box>
          <Typography variant="h6" gutterBottom>Advertiser Context</Typography>
          <Box sx={{ pl: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
            {crm.advertiser?.name && (
              <Typography variant="body2"><strong>Advertiser:</strong> {crm.advertiser.name}</Typography>
            )}
            {crm.igniteAdvertiser?.website && (
              <Typography variant="body2"><strong>Website:</strong> {crm.igniteAdvertiser.website}</Typography>
            )}
            {crm.igniteAdvertiser?.markets && (
              <Typography variant="body2"><strong>Markets:</strong> {crm.igniteAdvertiser.markets.join(", ")}</Typography>
            )}
            {crm.lineitem?.name && (
              <Typography variant="body2"><strong>Line Item:</strong> {crm.lineitem.name}</Typography>
            )}
            {crm.lineitem?.flightStart && (
              <Typography variant="body2">
                <strong>Flight:</strong> {crm.lineitem.flightStart} - {crm.lineitem.flightEnd || "ongoing"}
              </Typography>
            )}
            {crm.creativeOrder?.headlineBanner && (
              <Typography variant="body2"><strong>Previous Headline:</strong> {crm.creativeOrder.headlineBanner}</Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Background prompt */}
      {comp.backgroundPrompt && (
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>Background Prompt</Typography>
          <Typography variant="body2" sx={{ pl: 1, fontStyle: "italic" }}>{comp.backgroundPrompt}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default AdevalRefresh;
